import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import SelectPanel from '@lib/components/SelectPanel/SelectPanel';
import {
  AreaOption,
  SectorOption,
  SiteOption,
} from '@lib/components/SelectPanel/options';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getOptionsArrayValidation } from '@lib/utils/validationUtils';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import generateIdsFieldKey from '@lib/utils/generateIdsFieldKey';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { materialSymbolsOutlined } from '@lib/enums/common';
import { isFilterFieldListed } from '@lib/components/ModalTableMultiselect/utils/filterFieldVisibility';
import { FieldNames } from '../enums';

export default function useFilterFields({
  getSectorsLoadOptions,
  getSitesLoadOptions,
  getSiteAreasLoadOptions,
  getEquipmentLoadOptions,
  getFormCategoriesLoadOptions,
  disabledFields,
  hiddenFields,
}: {
  getSectorsLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getSiteAreasLoadOptions: GetLoadOptions;
  getEquipmentLoadOptions: GetLoadOptions;
  getFormCategoriesLoadOptions: GetLoadOptions;
  // Filter field names to render non-interactive — e.g. when a host form has
  // already pinned this picker to a specific site (via `filterOverrides` on
  // the *ModalTableMultiselect wrapper), Sector/Site stop making sense as
  // user-editable filters.
  disabledFields?: string[];
  // Filter field names to not render at all.
  hiddenFields?: string[];
}): FieldItem[] {
  const { t } = useTranslation();
  const isSectorsDisabled = isFilterFieldListed(
    disabledFields,
    FieldNames.Sectors,
  );
  const isSitesDisabled = isFilterFieldListed(disabledFields, FieldNames.Sites);
  const isEquipmentDisabled = isFilterFieldListed(
    disabledFields,
    FieldNames.Equipment,
  );
  return [
    {
      name: FieldNames.Sectors,
      label: t('sector'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(hiddenFields, FieldNames.Sectors),
      componentProps: {
        defaultOptions: false,
        getLoadOptions: getSectorsLoadOptions,
        renderOption: SectorOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        disabled: isSectorsDisabled,
        buttonProps: {
          leftIcon: 'share_location',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      fieldChangeCallBack: ({ setValue, name }: FieldChangeCallBackArgs) => {
        if (name === FieldNames.Sectors) {
          setValue(FieldNames.Sites, [], { shouldValidate: false });
          setValue(FieldNames.SiteArea, [], { shouldValidate: false });
          setValue(FieldNames.Equipment, [], { shouldValidate: false });
        }
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Sites,
      label: t('site'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(hiddenFields, FieldNames.Sites),
      componentProps: {
        defaultOptions: false,
        getLoadOptions: getSitesLoadOptions,
        renderOption: SiteOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        disabled: isSitesDisabled,
        buttonProps: {
          leftIcon: 'home_pin',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      getFieldKeyFn: (values: Values) => {
        const rawSectorIds = _get(values, FieldNames.Sectors, []) as Array<
          { value?: string } | string
        >;
        return generateIdsFieldKey(rawSectorIds, {
          prefix: `${FieldNames.Sectors}-sites`,
          emptyKey: `${FieldNames.Sectors}-no-sites`,
        });
      },
      fieldChangeCallBack: ({ setValue, name }: FieldChangeCallBackArgs) => {
        if (name === FieldNames.Sites) {
          setValue(FieldNames.SiteArea, [], { shouldValidate: false });
          setValue(FieldNames.Equipment, [], { shouldValidate: false });
        }
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.SiteArea,
      label: t('area'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(hiddenFields, FieldNames.SiteArea),
      componentProps: {
        defaultOptions: false,
        getLoadOptions: getSiteAreasLoadOptions,
        renderOption: AreaOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        disabled: isFilterFieldListed(disabledFields, FieldNames.SiteArea),
        buttonProps: {
          leftIcon: 'pin_drop',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      getFieldKeyFn: (values: Values) => {
        const rawSiteIds = _get(values, FieldNames.Sites, []) as Array<
          { value?: string } | string
        >;
        return generateIdsFieldKey(rawSiteIds, {
          prefix: `${FieldNames.Sites}-site-areas`,
          emptyKey: `${FieldNames.Sites}-no-site-areas`,
        });
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Equipment,
      label: t('asset'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(hiddenFields, FieldNames.Equipment),
      componentProps: {
        defaultOptions: false,
        getLoadOptions: getEquipmentLoadOptions,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        disabled: isEquipmentDisabled,
        buttonProps: {
          leftIcon: 'inventory',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      getFieldKeyFn: (values: Values) => {
        const rawSiteIds = _get(values, FieldNames.Sites, []) as Array<
          { value?: string } | string
        >;
        return generateIdsFieldKey(rawSiteIds, {
          prefix: `${FieldNames.Sites}-equipment`,
          emptyKey: `${FieldNames.Sites}-no-equipment`,
        });
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.FormCategories,
      label: t('form-category'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(hiddenFields, FieldNames.FormCategories),
      componentProps: {
        defaultOptions: false,
        getLoadOptions: getFormCategoriesLoadOptions,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        disabled: isFilterFieldListed(
          disabledFields,
          FieldNames.FormCategories,
        ),
        buttonProps: {
          leftIcon: 'category',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      isOptionalFn: () => false,
    },
  ];
}

export { FieldNames } from '../enums';
